import React, { useEffect, useState, useMemo } from 'react';
import { courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiList, FiX, FiImage, FiSearch } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useForm, Controller } from 'react-hook-form';

const CourseCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { register, handleSubmit, control, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      type: '',
      url: '',
      description: '',
      meta_title: '',
      meta_keyword: '',
      meta_description: '',
      position: 0,
      preference_id: ''
    }
  });
  
  const typeValue = watch('type');

  useEffect(() => {
    fetchCategories();
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await courseAPI.getCoursePreferences();
      if (response.data.success) {
        setPreferences(response.data.data.filter(p => p.status === 1));
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await courseAPI.getCourseTypes();
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching course categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setImageFile(null);
    setImagePreview('');
    reset({
      type: '',
      url: '',
      description: '',
      meta_title: '',
      meta_keyword: '',
      meta_description: '',
      position: 0
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setImageFile(null);
    setImagePreview(item.feature_image || '');
    reset({
      type: item.type || '',
      url: item.url || '',
      description: item.description || '',
      meta_title: item.meta_title || '',
      meta_keyword: item.meta_keyword || '',
      meta_description: item.meta_description || '',
      preference_id: item.preference_id || '',
      position: item.position || 0
    });
    setShowModal(true);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('type', data.type);
      submitData.append('url', data.url);
      submitData.append('description', data.description || '');
      submitData.append('meta_title', data.meta_title || '');
      submitData.append('meta_keyword', data.meta_keyword || '');
      submitData.append('meta_description', data.meta_description || '');
      submitData.append('position', data.position || 0);
      submitData.append('preference_id', data.preference_id || '');
      
      // Add image file if selected
      if (imageFile) {
        submitData.append('feature_image', imageFile);
      }

      let response;
      if (editingItem) {
        response = await courseAPI.updateCourseType(editingItem.id, submitData);
      } else {
        response = await courseAPI.createCourseType(submitData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setImageFile(null);
        setImagePreview('');
        reset();
        fetchCategories();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${item.type}"?`)) {
      return;
    }

    try {
      const response = await courseAPI.toggleCourseTypeStatus(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.type}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await courseAPI.deleteCourseType(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handlePositionChange = async (id, position) => {
    try {
      // Convert to integer and validate
      const positionValue = parseInt(position, 10);
      
      if (isNaN(positionValue) || positionValue < 0) {
        toast.error('Position must be a valid number greater than or equal to 0');
        return;
      }
      
      const response = await courseAPI.updateCourseTypePosition(id, positionValue);
      if (response.data.success) {
        toast.success('Position updated');
        fetchCategories();
      }
    } catch (error) {
      console.error('Position update error:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to update position');
    }
  };

  // Auto-generate URL from title
  useEffect(() => {
    if (typeValue) {
      const generatedUrl = typeValue.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setValue('url', generatedUrl);
    }
  }, [typeValue, setValue]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      e.target.value = '';
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      e.target.value = '';
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return categories;
    }

    const query = searchQuery.toLowerCase();
    return categories.filter(item => {
      const type = item.type?.toLowerCase() || '';
      const url = item.url?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return type.includes(query) || 
             url.includes(query) || 
             createdBy.includes(query);
    });
  }, [categories, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Course Categories</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage course types and categories</p>
        </div>
        <button 
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors min-h-[44px] w-full sm:w-auto" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Category
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by category name, URL, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
          </p>
        )}
      </div>

      {categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-gray-50 rounded-lg">
          <FiList size={64} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No categories yet</h3>
          <p className="text-gray-500 mb-6">Create your first course category to get started</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Category
          </button>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center bg-gray-50 rounded-lg">
          <FiSearch size={48} className="md:w-16 md:h-16 text-gray-300 mb-4" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">No categories found</h3>
          <p className="text-sm md:text-base text-gray-500 mb-6">
            No categories match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">URL</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Feature Image</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Position</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created By</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((item) => {
                  const canEdit = item.institute_id === user.institute_id || item.institute_id === null;
                  const isOwner = item.institute_id === user.institute_id;
                  
                  return (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-900">{item.id}</td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-xs md:text-sm font-medium text-gray-900">{item.type}</span>
                      </td>
                      <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{item.url}</td>
                      <td className="px-3 md:px-4 py-3">
                        {item.feature_image ? (
                          <img 
                            src={item.feature_image} 
                            alt={item.type}
                            className="w-12 h-12 md:w-16 md:h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded flex items-center justify-center">
                            <FiImage className="text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <input
                          type="number"
                          value={item.position}
                          onChange={(e) => handlePositionChange(item.id, e.target.value)}
                          className="w-16 md:w-20 px-2 py-1 border border-gray-300 rounded text-xs md:text-sm"
                          min="0"
                          disabled={!isOwner}
                        />
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-xs text-gray-600">{item.created_by_institute}</span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className={`inline-block px-2 md:px-3 py-1 text-xs font-medium rounded-full ${
                          item.status === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.status_label}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex justify-end items-center gap-1 md:gap-2">
                          {canEdit && isOwner && (
                            <>
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                                title="Edit"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(item)}
                                className={`p-2 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center ${
                                  item.status === 1 
                                    ? 'text-red-600 hover:bg-red-50' 
                                    : 'text-green-600 hover:bg-green-50'
                                }`}
                                title={item.status === 1 ? 'Deactivate' : 'Activate'}
                              >
                                {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                              </button>
                              <button
                                onClick={() => handleDelete(item)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                                title="Delete"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Course Category' : 'Add New Course Category'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('type', { required: 'Title is required' })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors ${
                    errors.type ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Engineering, Medical, Banking"
                />
                {errors.type && (
                  <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                )}
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Preference Category
                </label>
                <select
                  {...register('preference_id')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                >
                  <option value="">Select Preference (Optional)</option>
                  {preferences.map(pref => (
                    <option key={pref.id} value={pref.id}>
                      {pref.icon} {pref.name}
                    </option>
                  ))}
                </select>
                <small className="text-xs text-gray-500 mt-1 block">
                  Link this course type to a top-level preference category for better student filtering
                </small>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('url', { required: 'URL is required' })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors ${
                    errors.url ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., engineering"
                />
                {errors.url && (
                  <p className="text-red-500 text-sm mt-1">{errors.url.message}</p>
                )}
                <small className="text-xs text-gray-500 mt-1 block">Auto-generated from title. You can customize it.</small>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <CKEditor
                        editor={ClassicEditor}
                        data={field.value || ''}
                        onChange={(event, editor) => {
                          field.onChange(editor.getData());
                        }}
                        config={{
                          placeholder: 'Describe this course category...',
                          toolbar: [
                            'heading', '|',
                            'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                            'blockQuote', 'insertTable', '|',
                            'undo', 'redo'
                          ]
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feature Image
                </label>
                
                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title="Remove image"
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* File Upload */}
                  <div>
                    <label className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg cursor-pointer transition-colors w-fit">
                      <FiImage />
                      Choose Image File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imageFile && (
                      <p className="text-sm text-gray-600 mt-1">
                        Selected: {imageFile.name}
                      </p>
                    )}
                  </div>
                  
                 
                
                </div>
                
                <small className="text-xs text-gray-500 mt-2 block">
                  Max file size: 2MB. Supported formats: JPG, PNG, GIF, WebP
                </small>
              </div>

              <details className="mb-5 border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                  SEO Details (Optional)
                </summary>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                    <input
                      type="text"
                      {...register('meta_title')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      placeholder="SEO meta title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords</label>
                    <input
                      type="text"
                      {...register('meta_keyword')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      placeholder="keyword1, keyword2, keyword3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <Controller
                        name="meta_description"
                        control={control}
                        render={({ field }) => (
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value || ''}
                            onChange={(event, editor) => {
                              field.onChange(editor.getData());
                            }}
                            config={{
                              placeholder: 'SEO meta description',
                              toolbar: [
                                'bold', 'italic', 'link', '|',
                                'undo', 'redo'
                              ]
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </details>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCategories;

